import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface SchemaField {
  name: string;
  type: string;
  searchable: boolean;
  indexable: boolean;
}

export type DsType =
  | 'search_index'
  | 'blob_storage'
  | 'unstructured_store'
  | 'byo';

export type FilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'or_group';

export interface FilterParameter {
  name: string;
  source: string;
  required?: boolean;
  label?: string;
  description?: string | null;
}

export interface FilterClause {
  field?: string;
  op: FilterOp | string;
  value?: unknown;
  valueFrom?: string;
  value_from?: string;
  clauses?: FilterClause[];
}

export interface FilterConfig {
  parameters?: FilterParameter[];
  clauses?: FilterClause[];
}

export interface DataSource {
  dataSourceId: string;
  name: string;
  description: string | null;
  dsType: DsType;
  provider: string | null;
  connectionConfig: Record<string, unknown>;
  schemaDefinition: { fields: SchemaField[] } | null;
  filterConfig?: FilterConfig | null;
  status: string;
  recordCount: number | null;
  orgId: string;
  orgName: string;
  agentIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateDataSourceRequest {
  name: string;
  ds_type: DsType;
  description?: string;
  provider?: string;
  connection_config?: Record<string, unknown>;
  schema_definition?: { fields: SchemaField[] };
  filter_config?: FilterConfig | null;
}

export interface UpdateDataSourceRequest {
  name?: string;
  description?: string;
  connection_config?: Record<string, unknown>;
  schema_definition?: { fields: SchemaField[] };
  filter_config?: FilterConfig | null;
  status?: string;
}

export interface VerifyConnectionResult {
  verified: boolean;
  dataSourceId: string;
  schema: Record<string, unknown>;
  sampleRecords: Record<string, unknown>[];
  supportedOperations: string[];
}

export interface AnalyzeSchemaResult {
  fields: SchemaField[];
}

export const dataSourcesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDataSources: builder.query<DataSource[], void>({
      query: () => '/portal/data-sources',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ dataSources: DataSource[] }>(response);
        return normalized.dataSources ?? [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(
                (ds) =>
                  ({ type: 'DataSource' as const, id: ds.dataSourceId }),
              ),
              { type: 'DataSource', id: 'LIST' },
            ]
          : [{ type: 'DataSource', id: 'LIST' }],
    }),

    getDataSource: builder.query<DataSource, string>({
      query: (id) => `/portal/data-sources/${id}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<DataSource>(response),
      providesTags: (_res, _err, id) => [{ type: 'DataSource', id }],
    }),

    createDataSource: builder.mutation<DataSource, CreateDataSourceRequest>({
      query: (body) => ({
        url: '/portal/data-sources',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<DataSource>(response),
      invalidatesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),

    updateDataSource: builder.mutation<
      DataSource,
      { id: string; body: UpdateDataSourceRequest }
    >({
      query: ({ id, body }) => ({
        url: `/portal/data-sources/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<DataSource>(response),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'DataSource', id },
        { type: 'DataSource', id: 'LIST' },
      ],
    }),

    deleteDataSource: builder.mutation<{ deleted: boolean }, string>({
      query: (id) => ({
        url: `/portal/data-sources/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),

    verifyConnection: builder.mutation<VerifyConnectionResult, string>({
      query: (id) => ({
        url: `/portal/data-sources/${id}/verify-connection`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<VerifyConnectionResult>(response),
    }),

    updateAgentAssociations: builder.mutation<
      { dataSourceId: string; agentIds: string[] },
      { id: string; agentIds: string[] }
    >({
      query: ({ id, agentIds }) => ({
        url: `/portal/data-sources/${id}/agent-associations`,
        method: 'PUT',
        body: { agent_ids: agentIds },
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'DataSource', id }],
    }),

    analyzeSchema: builder.mutation<
      AnalyzeSchemaResult,
      { fields: { name: string; type: string }[] }
    >({
      query: (body) => ({
        url: '/portal/data-sources/analyze-schema',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<AnalyzeSchemaResult>(response),
    }),

    insertDataSourceRecords: builder.mutation<
      { dataSourceId: string; insertedCount: number; recordCount: number },
      { id: string; records: Record<string, unknown>[] }
    >({
      query: ({ id, records }) => ({
        url: `/portal/data-sources/${id}/records`,
        method: 'POST',
        body: { records },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          dataSourceId: string;
          insertedCount: number;
          recordCount: number;
        }>(response),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'DataSource', id },
        { type: 'DataSource', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetDataSourcesQuery,
  useGetDataSourceQuery,
  useCreateDataSourceMutation,
  useUpdateDataSourceMutation,
  useDeleteDataSourceMutation,
  useVerifyConnectionMutation,
  useUpdateAgentAssociationsMutation,
  useAnalyzeSchemaMutation,
  useInsertDataSourceRecordsMutation,
} = dataSourcesApi;
